/**
 * Shared auth state — module-level singleton.
 *
 * AuthContext writes here after every auth-me fetch.
 * API clients (apiClient, edgeFunctionClient, inline hooks) read here
 * as primary source, falling back to JWT user_metadata.
 *
 * Three states:
 * - `undefined`: auth-me hasn't run yet → fall back to JWT user_metadata
 * - `null`: auth-me explicitly returned no active business → honor the null
 * - `string`: auth-me returned a valid business ID → use it
 */

let _activeBusinessId: string | null | undefined = undefined

/**
 * Returns the active business ID from the most recent auth-me response.
 * `undefined` means auth-me hasn't populated this yet (callers should fall back to JWT).
 * `null` means auth-me explicitly cleared the selection (callers must NOT fall back).
 */
export function getActiveBusinessId(): string | null | undefined {
  return _activeBusinessId
}

export function setActiveBusinessId(id: string | null): void {
  _activeBusinessId = id
}

/**
 * Reset to pre-auth-me state (used on sign-out).
 * After this, callers will fall back to JWT user_metadata until auth-me runs again.
 */
export function resetActiveBusinessId(): void {
  _activeBusinessId = undefined
}

/**
 * Resolve active business ID with proper three-state semantics.
 * Use this in API clients / hooks that build the x-active-business-id header.
 *
 * @param jwtFallback - value from session.user.user_metadata.active_business_id
 * @returns string (use it), null (no business — don't send header)
 */
export function resolveActiveBusinessId(jwtFallback: string | null | undefined): string | null {
  // If auth-me has run, trust its result (even if null — stale selection was cleared)
  if (_activeBusinessId !== undefined) {
    return _activeBusinessId
  }
  // auth-me hasn't run yet — fall back to JWT
  return jwtFallback ?? null
}
